import randomId from './randomId';
import { TOOLS_IDS } from './constants';

/** Rect behind a text template preset — not selectable; clicks pass through to text. */
export const TEMPLATE_CARD_BACKGROUND_KEY = 'templateCardBackground';

export const getAnnotationsInGroup = (annotations, groupId) => {
  if (!groupId) {
    return [];
  }
  return Object.values(annotations).filter(
    (annotation) => annotation.groupId === groupId,
  );
};

export const getGroupMemberIds = (annotations, annotationId) => {
  const annotation = annotations[annotationId];
  if (!annotation?.groupId) {
    return [annotationId];
  }
  return getAnnotationsInGroup(annotations, annotation.groupId).map(
    (member) => member.id,
  );
};

export const getIdsToMoveWithPrimary = (
  annotations,
  primaryId,
  selectionsIds = [],
) => {
  const primary = annotations[primaryId];
  if (!primary || primary.locked) {
    return [];
  }

  if (primary.groupId) {
    const ids = getGroupMemberIds(annotations, primaryId).filter(
      (id) => !annotations[id]?.locked,
    );
    if (ids.length > 0) {
      return ids;
    }
  }

  const selectedMovable = selectionsIds.filter(
    (id) => annotations[id] && !annotations[id].locked,
  );
  if (selectedMovable.length > 1 && selectedMovable.includes(primaryId)) {
    return selectedMovable;
  }

  return [primaryId];
};

export const createGroupId = () => randomId('group');

/** Two-member group: one text layer + one card rect (picker or saved template). */
export const isTextBackgroundCardGroup = (annotations, groupId) => {
  if (!groupId || !annotations) {
    return false;
  }
  const members = getAnnotationsInGroup(annotations, groupId);
  if (members.length !== 2) {
    return false;
  }
  const textMembers = members.filter((m) => m.name === TOOLS_IDS.TEXT);
  const cardMembers = members.filter(
    (m) =>
      m[TEMPLATE_CARD_BACKGROUND_KEY] || m.name === TOOLS_IDS.RECT,
  );
  return textMembers.length === 1 && cardMembers.length === 1;
};

export const getTextBackgroundCardTextId = (annotations, groupId) => {
  const members = getAnnotationsInGroup(annotations, groupId);
  return members.find((m) => m.name === TOOLS_IDS.TEXT)?.id ?? null;
};

/** Ungrouped card rect: pick the text visually inside the card bounds. */
export const findTextIdForCardBackground = (annotations, cardId) => {
  const card = annotations?.[cardId];
  if (!card?.[TEMPLATE_CARD_BACKGROUND_KEY]) {
    return null;
  }
  const cardRight = (card.x ?? 0) + (card.width ?? 0);
  const cardBottom = (card.y ?? 0) + (card.height ?? 0);
  const match = Object.values(annotations).find((candidate) => {
    if (candidate.name !== TOOLS_IDS.TEXT) {
      return false;
    }
    const tx = candidate.x ?? 0;
    const ty = candidate.y ?? 0;
    return (
      tx >= (card.x ?? 0) &&
      ty >= (card.y ?? 0) &&
      tx <= cardRight &&
      ty <= cardBottom
    );
  });
  return match?.id ?? null;
};

export const tagTextBackgroundCardRects = (annotations = {}) => {
  const groupIds = new Set(
    Object.values(annotations)
      .map((a) => a.groupId)
      .filter(Boolean),
  );
  let changed = false;
  const next = { ...annotations };

  groupIds.forEach((groupId) => {
    if (!isTextBackgroundCardGroup(next, groupId)) {
      return;
    }
    getAnnotationsInGroup(next, groupId).forEach((member) => {
      if (member.name !== TOOLS_IDS.RECT || member[TEMPLATE_CARD_BACKGROUND_KEY]) {
        return;
      }
      changed = true;
      next[member.id] = {
        ...member,
        [TEMPLATE_CARD_BACKGROUND_KEY]: true,
      };
    });
  });

  return changed ? next : annotations;
};
